/**
 * JobsList Component
 *
 * List view for jobs across all tenants with:
 * - Pagination and sorting
 * - Search by title
 * - Filters by status, tenant, date range
 * - Drill-down to job detail with candidates and outreach
 *
 * Uses service-role queries to bypass RLS.
 *
 * ------------------------------------------------------------------
 * Migrated for "Implement RTK Query hooks and caching for all API
 * endpoints": previously fetched via a hand-rolled `useEffect` + local
 * `useState`, and rendered hard-coded mock `AdminJobInfo[]` with
 * client-side search/filter/pagination behind a `// TODO: Call GET
 * /api/admin/jobs with filters` comment. Now:
 * - Data fetching: `useAdminJobs()` (`@/lib/api/apiClient`) — a
 *   TanStack Query hook, Zod-validated response, server-side
 *   search/filter/sort/pagination via real query params.
 * - Filter/sort/pagination state: `useApiStore` (`@/stores/apiStore`,
 *   Zustand) instead of four separate `useState` calls.
 * - Loading state: ShadCN `Skeleton`.
 * - Error state: `sonner` `toast.error` + inline retry.
 *
 * ------------------------------------------------------------------
 * UPDATE (GitHub issue #499, "E2E: Admin dashboard — outreach metrics,
 * company filter, job table"): that ticket's "Company Filter" test-case
 * group ("Dropdown populated with companies" / "Selecting company
 * filters job table") and "Sourcing Jobs Table" test-case group's full
 * column set (candidates/queued/sent/followups/opened/bounced/replied/
 * shortlisted/declined — vs. this grid's actual sourced/screened/sent/
 * replies) are NOT implemented here.
 *
 * TODO(schema): `AdminJobInfo` (`@/lib/validation/admin`) has no
 * `company`/`company_name` field, and neither does its real backing
 * table (`jobs` — see `db/schema.ts`; `tenant_id` identifies the owning
 * customer account, not a "company" a job can be filtered by across
 * customers). There's also no `GET /api/admin/companies`-shaped endpoint
 * to populate a filter dropdown from, and no per-status outreach
 * counters (queued/followups/opened/bounced/shortlisted/declined) beyond
 * the aggregate `outreach_sent`/`replies_received` this grid already
 * shows. Per `CLAUDE.md`'s "Schema authority" rule ("never hand-edit
 * ... without a matching migration"), adding these is a real product
 * change (new migration + `db/schema.ts` + `apps/workers/src/api/admin/
 * list.ts` + this schema/component), out of scope for this E2E-test
 * issue — `e2e/admin-dashboard.spec.ts` marks the corresponding QA cases
 * `test.fixme(...)` rather than asserting against fields that don't
 * exist, so the gap is visible in `playwright test` output (not just a
 * comment) until a follow-up issue adds them.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdminJobs, reportApiError } from '@/lib/api/apiClient';
import { useApiStore } from '@/stores/apiStore';
import { Skeleton } from '@/components/ui/skeleton';
import type { AdminJobsQueryParams } from '@/lib/validation/admin';

export default function JobsList() {
  const navigate = useNavigate();

  const filters = useApiStore((state) => state.adminJobsFilters);
  const setFilters = useApiStore((state) => state.setAdminJobsFilters);

  const { data, isLoading, isError, error, refetch, isFetching } = useAdminJobs(filters);

  useEffect(() => {
    if (isError) {
      reportApiError(error, 'Failed to load jobs');
    }
  }, [isError, error]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 space-y-4" aria-busy="true">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-full" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">Error Loading Jobs</div>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Failed to fetch jobs'}
          </p>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-4 px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasMore = filters.offset + filters.limit < data.total_count;
  const hasPrev = filters.offset > 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold">Jobs</h1>
            <button
              onClick={() => navigate('/admin')}
              className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Filters and Search */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-4">
            <input
              type="text"
              placeholder="Search by title..."
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value, offset: 0 })}
              className="px-3 py-2 border border-border rounded-md text-sm"
            />

            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({
                  status: e.target.value as AdminJobsQueryParams['status'],
                  offset: 0,
                })
              }
              className="px-3 py-2 border border-border rounded-md text-sm"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="CLOSED">Closed</option>
            </select>

            <select
              value={filters.sortBy}
              onChange={(e) =>
                setFilters({ sortBy: e.target.value as AdminJobsQueryParams['sortBy'] })
              }
              className="px-3 py-2 border border-border rounded-md text-sm"
            >
              <option value="created_at">Sort by Created</option>
              <option value="updated_at">Sort by Updated</option>
              <option value="title">Sort by Title</option>
            </select>

            <div className="text-sm text-muted-foreground pt-2">
              Showing {data.data.length} of {data.total_count} jobs
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {data.data.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No jobs found</p>
          </div>
        ) : (
          <>
            {/* Grid View */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
              {data.data.map((job) => (
                <div
                  key={job.uuid}
                  data-testid="admin-job-row"
                  className="p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/admin/jobs/${job.uuid}`, { state: { job } })}
                >
                  <div className="mb-2">
                    <h3 className="font-semibold text-base" data-testid="admin-job-row-title">
                      {job.title}
                    </h3>
                    <p className="text-xs text-muted-foreground">By: {job.owner_name}</p>
                  </div>

                  <div className="mb-3">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        job.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : job.status === 'DRAFT'
                            ? 'bg-blue-100 text-blue-800'
                            : job.status === 'PAUSED'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {job.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                    <div>
                      <p className="text-muted-foreground">Sourced</p>
                      <p className="font-semibold">{job.sourced_count}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Screened</p>
                      <p className="font-semibold">{job.screened_count}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Sent</p>
                      <p className="font-semibold">{job.outreach_sent}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Replies</p>
                      <p className="font-semibold">{job.replies_received}</p>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                    Created: {new Date(job.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center">
              <button
                onClick={() => setFilters({ offset: Math.max(0, filters.offset - filters.limit) })}
                disabled={!hasPrev}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>

              <div className="text-sm text-muted-foreground">
                Page {Math.floor(filters.offset / filters.limit) + 1}
              </div>

              <button
                onClick={() => setFilters({ offset: filters.offset + filters.limit })}
                disabled={!hasMore}
                className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
